// Admin CRUD: suppliers (name, website) and multiple contacts (name, email, phone per contact)

import { useState } from 'react';
import { useSuppliers } from '@/hooks/use-store';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Supplier, SupplierContact } from '@/lib/store-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Plus, Pencil, Trash2 } from 'lucide-react';

const SUPPLIERS_KEY = ['store', 'lookups', 'suppliers'];

function invalidation(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: SUPPLIERS_KEY });
}

export default function AdminSuppliers() {
  const queryClient = useQueryClient();
  const { data: suppliers, isLoading } = useSuppliers();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  // New contacts when creating a supplier (no id yet)
  const [newContacts, setNewContacts] = useState<{ name: string; email: string; phone: string }[]>([]);
  // Inline form for adding one contact (edit mode or before save in create)
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  // Editing a single contact (id for edit mode)
  const [editingContact, setEditingContact] = useState<SupplierContact | null>(null);
  const [editContactName, setEditContactName] = useState('');
  const [editContactEmail, setEditContactEmail] = useState('');
  const [editContactPhone, setEditContactPhone] = useState('');
  const { toast } = useToast();

  const saveSupplierMutation = useMutation({
    mutationFn: async () => {
      const row = { name: name.trim(), website: website.trim() || null };
      if (editing) {
        const { error } = await supabase.from('suppliers').update(row).eq('id', editing.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from('suppliers').insert(row).select('id').single();
        if (error) throw error;
        const supplierId = data?.id;
        if (supplierId && newContacts.length > 0) {
          const { error: err2 } = await supabase.from('supplier_contacts').insert(
            newContacts.map((c) => ({
              supplier_id: supplierId,
              name: c.name.trim() || null,
              email: c.email.trim() || null,
              phone: c.phone.trim() || null,
            }))
          );
          if (err2) throw err2;
        }
      }
    },
    onSuccess: () => {
      invalidation(queryClient);
      setOpen(false);
      setEditing(null);
      resetForm();
      toast({ title: editing ? 'Supplier updated' : 'Supplier created' });
    },
    onError: (e) => toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' }),
  });

  const addContactMutation = useMutation({
    mutationFn: async ({ supplierId, name: n, email: e, phone: p }: { supplierId: string; name: string; email: string; phone: string }) => {
      const { error } = await supabase.from('supplier_contacts').insert({
        supplier_id: supplierId,
        name: n.trim() || null,
        email: e.trim() || null,
        phone: p.trim() || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidation(queryClient);
      setContactName('');
      setContactEmail('');
      setContactPhone('');
      toast({ title: 'Contact added' });
    },
    onError: (err) => toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed', variant: 'destructive' }),
  });

  const updateContactMutation = useMutation({
    mutationFn: async ({ id, name: n, email: e, phone: p }: { id: string; name: string; email: string; phone: string }) => {
      const { error } = await supabase
        .from('supplier_contacts')
        .update({ name: n.trim() || null, email: e.trim() || null, phone: p.trim() || null })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidation(queryClient);
      setEditingContact(null);
      toast({ title: 'Contact updated' });
    },
    onError: (err) => toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed', variant: 'destructive' }),
  });

  const deleteContactMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('supplier_contacts').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidation(queryClient);
      toast({ title: 'Contact deleted' });
    },
    onError: (err) => toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed', variant: 'destructive' }),
  });

  const deleteSupplierMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('suppliers').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidation(queryClient);
      setOpen(false);
      setEditing(null);
      resetForm();
      toast({ title: 'Supplier deleted' });
    },
    onError: (e) => toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' }),
  });

  function resetForm() {
    setName('');
    setWebsite('');
    setNewContacts([]);
    setContactName('');
    setContactEmail('');
    setContactPhone('');
    setEditingContact(null);
    setEditing(null);
  }

  function openEdit(supplier: Supplier) {
    setEditing(supplier);
    setName(supplier.name);
    setWebsite(supplier.website ?? '');
    setNewContacts([]);
    setContactName('');
    setContactEmail('');
    setContactPhone('');
    setEditingContact(null);
    setOpen(true);
  }

  function handleAddContact() {
    if (editing) {
      addContactMutation.mutate({
        supplierId: editing.id,
        name: contactName,
        email: contactEmail,
        phone: contactPhone,
      });
    } else {
      setNewContacts((prev) => [...prev, { name: contactName, email: contactEmail, phone: contactPhone }]);
      setContactName('');
      setContactEmail('');
      setContactPhone('');
    }
  }

  function openEditContact(c: SupplierContact) {
    setEditingContact(c);
    setEditContactName(c.name ?? '');
    setEditContactEmail(c.email ?? '');
    setEditContactPhone(c.phone ?? '');
  }

  const allContactsDisplay = editing ? (editing.supplier_contacts ?? []) : newContacts.map((c, i) => ({ id: `new-${i}`, name: c.name, email: c.email, phone: c.phone }));

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Suppliers</h1>
      <p className="text-sm text-muted-foreground mb-4">
        Proveedores asociados a cada origen. Un supplier puede tener varios contactos (nombre, email, teléfono).
      </p>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
        <DialogTrigger asChild>
          <Button onClick={() => { resetForm(); setEditing(null); }}>Add supplier</Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit supplier' : 'New supplier'}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Supplier name" />
              </div>
              <div>
                <Label>Website</Label>
                <Input type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://..." />
              </div>
            </div>

            <div>
              <Label className="block mb-2">Contactos</Label>
              {allContactsDisplay.length > 0 && (
                <Table className="border rounded-md mb-2">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Teléfono</TableHead>
                      <TableHead className="w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allContactsDisplay.map((c, idx) => {
                      const isExisting = editing && 'id' in c && typeof (c as SupplierContact).id === 'string' && !(c as SupplierContact).id.startsWith('new-');
                      return (
                        <TableRow key={isExisting ? (c as SupplierContact).id : `new-${idx}`}>
                          <TableCell>{c.name || '—'}</TableCell>
                          <TableCell>
                            {c.email ? (
                              <a href={`mailto:${c.email}`} className="text-primary hover:underline text-sm">{c.email}</a>
                            ) : '—'}
                          </TableCell>
                          <TableCell>{c.phone || '—'}</TableCell>
                          {editing && (
                            <TableCell>
                              {isExisting ? (
                                <>
                                  <Button variant="ghost" size="sm" onClick={() => openEditContact(c as SupplierContact)}><Pencil className="h-3 w-3" /></Button>
                                  <Button variant="ghost" size="sm" onClick={() => deleteContactMutation.mutate((c as SupplierContact).id)}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                                </>
                              ) : null}
                            </TableCell>
                          )}
                          {!editing && (
                            <TableCell>
                              <Button variant="ghost" size="sm" onClick={() => setNewContacts((prev) => prev.filter((_, i) => i !== idx))}>
                                <Trash2 className="h-3 w-3 text-destructive" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}

              {/* Add contact: inline form */}
              <div className="flex flex-wrap items-end gap-2 border rounded-md p-2 bg-muted/40">
                <Input className="w-32" placeholder="Nombre" value={contactName} onChange={(e) => setContactName(e.target.value)} />
                <Input className="w-40" type="email" placeholder="Email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
                <Input className="w-36" placeholder="Teléfono" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
                <Button type="button" size="sm" variant="secondary" onClick={handleAddContact} className="gap-1" disabled={addContactMutation.isPending}>
                  <Plus className="h-3 w-3" />
                  Añadir contacto
                </Button>
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={() => saveSupplierMutation.mutate()} disabled={saveSupplierMutation.isPending || !name.trim()}>
                {saveSupplierMutation.isPending ? 'Saving...' : 'Save supplier'}
              </Button>
              {editing && (
                <Button variant="destructive" onClick={() => deleteSupplierMutation.mutate(editing.id)} disabled={deleteSupplierMutation.isPending}>
                  Delete supplier
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit contact modal */}
      <Dialog open={!!editingContact} onOpenChange={(o) => { if (!o) setEditingContact(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit contact</DialogTitle></DialogHeader>
          {editingContact && (
            <div className="space-y-4 pt-4">
              <div>
                <Label>Nombre</Label>
                <Input value={editContactName} onChange={(e) => setEditContactName(e.target.value)} />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={editContactEmail} onChange={(e) => setEditContactEmail(e.target.value)} />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input value={editContactPhone} onChange={(e) => setEditContactPhone(e.target.value)} />
              </div>
              <Button
                onClick={() =>
                  updateContactMutation.mutate({
                    id: editingContact.id,
                    name: editContactName,
                    email: editContactEmail,
                    phone: editContactPhone,
                  })
                }
                disabled={updateContactMutation.isPending}
              >
                Save
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {isLoading ? (
        <p className="text-secondary">Loading...</p>
      ) : (
        <Table className="mt-6">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Contactos</TableHead>
              <TableHead>Website</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(suppliers ?? []).map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell className="text-muted-foreground text-sm max-w-[320px]">
                  {(s.supplier_contacts ?? []).length === 0 ? (
                    '—'
                  ) : (
                    <ul className="list-disc list-inside space-y-0.5">
                      {(s.supplier_contacts ?? []).map((c) => (
                        <li key={c.id}>
                          {[c.name, c.email, c.phone].filter(Boolean).join(' · ') || '—'}
                        </li>
                      ))}
                    </ul>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {s.website ? (
                    <a href={s.website.startsWith('http') ? s.website : `https://${s.website}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate max-w-[180px] inline-block">
                      {s.website}
                    </a>
                  ) : '—'}
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(s)}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteSupplierMutation.mutate(s.id)}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
